import { useEffect, useState } from 'react';
import { BarChart3, Check, ChevronDown, ChevronRight, FileText, Plus, Users } from 'lucide-react';
import { useMappingsStore } from '@/features/form-builder/store/mappings.store';
import { useFormBuilderStore } from '@/features/form-builder/store/form-builder.store';
import { useBankAnalysisMappingStore } from '@/features/form-builder/store/bank-analysis-mapping.store';

interface BankAnalysisMetricsNavigatorSectionProps {
  className?: string;
}

// Purpose: Left-pane navigator section for bank analysis metric mappings.
const BankAnalysisMetricsNavigatorSection = ({ className = '' }: BankAnalysisMetricsNavigatorSectionProps) => {
  const { currentFieldMappingId } = useMappingsStore();
  const { setActiveFieldId } = useFormBuilderStore();
  const {
    activeMetricSelection,
    setActiveMetricSelection,
    clearActiveMetric,
    loadForMappingId,
    metricFieldsByKey,
    metrics,
    isLoading,
    statementPeriodCount,
    addStatementPeriod,
  } = useBankAnalysisMappingStore();

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['aggregate', 'competitor', 'statementPeriod'])
  );
  const [expandedPeriods, setExpandedPeriods] = useState<Set<number>>(new Set([0]));

  useEffect(() => {
    if (!currentFieldMappingId) {
      clearActiveMetric();
      return;
    }
    void loadForMappingId(currentFieldMappingId);
  }, [currentFieldMappingId, loadForMappingId, clearActiveMetric]);

  // Purpose: Reset expansion when switching mappings to avoid confusing state carry-over.
  useEffect(() => {
    setExpandedCategories(new Set(['aggregate', 'competitor', 'statementPeriod']));
    setExpandedPeriods(new Set([0]));
  }, [currentFieldMappingId]);

  if (!currentFieldMappingId) {
    return null;
  }

  // Purpose: Selection + storage key are composite (metricId + sourceIndex).
  const makeMetricKey = (metricId: string, sourceIndex?: number): string => {
    return `${metricId}::${Number.isInteger(sourceIndex) ? sourceIndex : 'na'}`;
  };

  const isSelected = (metricId: string, sourceIndex?: number): boolean => {
    return (
      activeMetricSelection?.metricId === metricId &&
      (activeMetricSelection?.sourceIndex ?? undefined) === (sourceIndex ?? undefined)
    );
  };

  const getMetricMapped = (metricId: string, sourceIndex?: number): boolean => {
    const field = metricFieldsByKey[makeMetricKey(metricId, sourceIndex)];
    return Boolean(field?.fieldApiName);
  };

  const selectMetric = (metricId: string, sourceIndex?: number) => {
    // Purpose: Selecting a metric switches the right pane away from form fields.
    setActiveFieldId(null);
    setActiveMetricSelection({ metricId, sourceIndex });
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const togglePeriod = (index: number) => {
    setExpandedPeriods((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const aggregateMetrics = metrics.filter((m) => (m.category || 'aggregate') === 'aggregate');
  const competitorMetrics = metrics.filter((m) => m.category === 'competitor');
  const statementPeriodMetrics = metrics.filter((m) => m.category === 'statementPeriod');

  const mappedAggregateCount = aggregateMetrics.filter((m) => getMetricMapped(m.id)).length;
  const mappedCompetitorCount = competitorMetrics.filter((m) => getMetricMapped(m.id)).length;
  const mappedStatementCount = statementPeriodMetrics.reduce((sum, m) => {
    let mapped = 0;
    for (let i = 0; i < statementPeriodCount; i += 1) {
      if (getMetricMapped(m.id, i)) mapped += 1;
    }
    return sum + mapped;
  }, 0);

  const totalAggregateCount = aggregateMetrics.length;
  const totalCompetitorCount = competitorMetrics.length;
  const totalStatementCount = statementPeriodMetrics.length * statementPeriodCount;

  return (
    <div className={className}>
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <BarChart3 className="h-4 w-4 mr-2" />
          Bank Statements
        </h2>

        <div className="space-y-2">
          {/* General metrics (aggregate) */}
          <div className="border rounded-md">
            <button
              type="button"
              onClick={() => toggleCategory('aggregate')}
              className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-md"
            >
              <div className="flex items-center gap-2">
                {expandedCategories.has('aggregate') ? (
                  <ChevronDown className="h-5 w-5" />
                ) : (
                  <ChevronRight className="h-5 w-5" />
                )}
                <span className="font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  General Metrics
                </span>
                <span className="text-sm text-green-600">
                  ({mappedAggregateCount}/{totalAggregateCount})
                </span>
              </div>
            </button>

            {expandedCategories.has('aggregate') && (
              <div className="border-t border-gray-200 p-3">
                {isLoading && aggregateMetrics.length === 0 ? (
                  <div className="text-sm text-gray-500">Loading metrics...</div>
                ) : (
                  <ul className="space-y-1">
                    {aggregateMetrics.map((m) => {
                      const active = isSelected(m.id);
                      const mapped = getMetricMapped(m.id);
                      return (
                        <li key={m.id}>
                          <button
                            type="button"
                            onClick={() => selectMetric(m.id)}
                            className={`w-full text-left px-2 py-2 rounded flex items-center justify-between transition-colors ${
                              active ? 'bg-blue-100 text-blue-900' : 'hover:bg-gray-100'
                            }`}
                          >
                            <span className="text-sm truncate">{m.label}</span>
                            {mapped && <Check className="h-4 w-4 text-green-600" aria-label="Mapped" />}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Competitors */}
          <div className="border rounded-md">
            <button
              type="button"
              onClick={() => toggleCategory('competitor')}
              className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-md"
            >
              <div className="flex items-center gap-2">
                {expandedCategories.has('competitor') ? (
                  <ChevronDown className="h-5 w-5" />
                ) : (
                  <ChevronRight className="h-5 w-5" />
                )}
                <span className="font-medium flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Competitors
                </span>
                <span className="text-sm text-green-600">
                  ({mappedCompetitorCount}/{totalCompetitorCount})
                </span>
              </div>
            </button>

            {expandedCategories.has('competitor') && (
              <div className="border-t border-gray-200 p-3">
                {isLoading && competitorMetrics.length === 0 ? (
                  <div className="text-sm text-gray-500">Loading metrics...</div>
                ) : (
                  <ul className="space-y-1">
                    {competitorMetrics.map((m) => {
                      const active = isSelected(m.id);
                      const mapped = getMetricMapped(m.id);
                      return (
                        <li key={m.id}>
                          <button
                            type="button"
                            onClick={() => selectMetric(m.id)}
                            className={`w-full text-left px-2 py-2 rounded flex items-center justify-between transition-colors ${
                              active ? 'bg-blue-100 text-blue-900' : 'hover:bg-gray-100'
                            }`}
                          >
                            <span className="text-sm truncate">{m.label}</span>
                            {mapped && <Check className="h-4 w-4 text-green-600" aria-label="Mapped" />}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Statement periods (repeatable instances) */}
          <div className="border rounded-md">
            <button
              type="button"
              onClick={() => toggleCategory('statementPeriod')}
              className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-md"
            >
              <div className="flex items-center gap-2">
                {expandedCategories.has('statementPeriod') ? (
                  <ChevronDown className="h-5 w-5" />
                ) : (
                  <ChevronRight className="h-5 w-5" />
                )}
                <span className="font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Statement Periods
                </span>
                <span className="text-sm text-green-600">
                  ({mappedStatementCount}/{totalStatementCount})
                </span>
              </div>
            </button>

            {expandedCategories.has('statementPeriod') && (
              <div className="border-t border-gray-200 p-3">
                {statementPeriodMetrics.length === 0 ? (
                  <div className="text-sm text-gray-500">No statement-period metrics available.</div>
                ) : (
                  <div className="space-y-2">
                    {Array.from({ length: statementPeriodCount }, (_, idx) => {
                      const expanded = expandedPeriods.has(idx);
                      const mappedInPeriod = statementPeriodMetrics.filter((m) => getMetricMapped(m.id, idx)).length;
                      return (
                        <div key={`statement-period-${idx}`} className="border rounded-md">
                          <button
                            type="button"
                            onClick={() => togglePeriod(idx)}
                            className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 rounded-md"
                          >
                            <div className="flex items-center gap-2">
                              {expanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                              <span className="text-sm font-medium">Statement Period {idx + 1}</span>
                              <span className="text-xs text-green-600">
                                ({mappedInPeriod}/{statementPeriodMetrics.length})
                              </span>
                            </div>
                          </button>

                          {expanded && (
                            <div className="border-t border-gray-200 p-2">
                              <ul className="space-y-1">
                                {statementPeriodMetrics.map((m) => {
                                  const active = isSelected(m.id, idx);
                                  const mapped = getMetricMapped(m.id, idx);
                                  return (
                                    <li key={`${m.id}::${idx}`}>
                                      <button
                                        type="button"
                                        onClick={() => selectMetric(m.id, idx)}
                                        className={`w-full text-left px-2 py-2 rounded flex items-center justify-between transition-colors ${
                                          active ? 'bg-blue-100 text-blue-900' : 'hover:bg-gray-100'
                                        }`}
                                      >
                                        <span className="text-sm truncate">{m.label}</span>
                                        {mapped && (
                                          <Check className="h-4 w-4 text-green-600" aria-label="Mapped" />
                                        )}
                                      </button>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    <button
                      type="button"
                      onClick={() => addStatementPeriod()}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded border border-dashed text-sm text-gray-600 hover:bg-gray-50"
                    >
                      <Plus className="h-4 w-4" />
                      Add Statement Period
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankAnalysisMetricsNavigatorSection;
